import React, { useEffect, useRef, useState, useMemo } from 'react';
import ReactToolTip from 'react-tooltip';
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import processParams from "utils/processParams";
import validate from "utils/validate";
import FormDrawer from "components/__new/common/form-drawer";
import DashboardBox from "components/__new/common/DashboardBox";
import NumberFormat from "utils/numberFormat";
import SearchBox from "components/__new/common/SearchBox";
import DownloadAsCSV from "components/__new/common/DownloadAsCSV";
import Print from "components/__new/common/Print";
import Table from "components/__new/common/Table";
import { TableContent } from "components/__new/common/TableContent";
import Pagination from "components/__new/common/pagination";
import Maybe from "components/__new/common/Maybe";
import { ButtonLoader, Input, SelectInput } from "components/__new/common/form";
import * as employeesActions from "store/entities/employees/action";
import * as banksActions from "store/ui/banks/action";
import * as groupsActions from "store/entities/groups/action";
import ReactSelect from 'react-select';
import { reactSelectStyles } from 'utils/reactSelectStyles';
import useBanks from 'hooks/useBanks';
import useLoginInfo from 'hooks/useLoginInfo';


const ListCRM = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const {userInfo} = useLoginInfo();
    const {urlQueryParams} = useUrlQueryParams();
    const { employeesLoading, employees, employeesDownloadLoading, employeesDownload, employeeCreateLoading, employeeCreateSuccess } = useSelector((s) => s.entities.employees);
    const { banks, banksLoading } = useBanks(userInfo?.token);
    const { getAllGroupsLoading: groupsLoading, allGroups: groups } = useSelector((s) => s.entities.groups);


    const downloadAsCSVRef = useRef();
    const printComponentRef = useRef();
    const [isModalOpenActivate, setIsModalOpenActivate] = useState(false);
    const [employee, setEmployee] = useState({});
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 20,
    });
    const [form, setForm] = useState({
        fullName: "",
        email: "",
        phoneNumber: "",
        jobTitle: "",
        employeeId: "",
        groupPolicyId: "general-group",
        netMonthlySalary: "",
        bankName: "",
        bankCode: "",
        bankAccountNumber: "",
    })
    
    const banksData = useMemo(() => {
        return banks?.sort((a, b) => (a?.bankName || a?.institutionName)?.localeCompare(b?.bankName || b?.institutionName))?.map((singleBank) => ({
            label: singleBank?.bankName || singleBank?.institutionName,
            value: singleBank?.bankCode || singleBank?.institutionCode,
        })) || [];
    }, [banks])


    const getEmployees = () => {
        let params = {...listParams};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 20, "limit");
        params = processParams(urlQueryParams, params, "search", "string", "", "fullName");
        params = processParams(urlQueryParams, params, "status", "string", "", "status");

        setListParams(params);        
        dispatch(employeesActions.getAllCRMEmployees(params));
    }


    const getBankName = () => {
        const bankData = banks;
        for (var i = 0; i < bankData?.length; i++){
            if (bankData[i].code === form.bankCode){
                return (bankData[i].name);
            }
        }
        return null;
    }


    const getFullName = (firstName, middleName, lastName) => {
        if (middleName !== null && middleName !== undefined){
            return (firstName + " " + middleName + " " + lastName);
        }
        return (firstName + " " + lastName);
    }


    const getEmailAddress = (emails) => {
        return (emails.length >= 1 ? emails[0].data : "---");
    }


    const getPhoneNumbers = (phone_numbers) => {
        return (phone_numbers.length >= 1 ? phone_numbers[0].data : "---");
    }

    
    const openModalActivate = (employee) => {
        const createEmployeeForm = {
            ...form,
            fullName: getFullName(employee.first_name, employee.middle_name, employee.last_name),
            email: getEmailAddress(employee.emails),
            phoneNumber: getEmailAddress(employee.phone_numbers),
            jobTitle: "",
            employeeId: "",
            groupPolicyId: "general-group",
            netMonthlySalary: "",
            bankName: "",
            bankCode: "",
            bankAccountNumber: "",
        }
        setEmployee(employee);
        setForm(createEmployeeForm);
        setIsModalOpenActivate(true);
    }


    const submitForm = async e => {
        e.preventDefault();
        const validateForm = {...form};
        delete validateForm["employeeId"];
        const errors = validate(validateForm);
        if (errors){
            for (var x in errors) {
                toast.error(errors[x]);
            };
            return;
        }
        else{
            const bankName = getBankName();
            const params = {...form, bankName};
            setForm(params);
            dispatch(employeesActions.createEmployee(params));
        }
    }


    useEffect(() => {
        getEmployees();
        // eslint-disable-next-line
    }, [location]);


    useEffect(() => {
        dispatch(groupsActions.getAllGroups());
        dispatch(banksActions.getAllBanks());
        // eslint-disable-next-line
    }, []);


    useEffect(() => {
        if (employeeCreateSuccess === true){
            dispatch(employeesActions.resetEmployeeCreateSuccess());
            setIsModalOpenActivate(false);
            getEmployees();
        }
        // eslint-disable-next-line
    }, [employeeCreateSuccess]);


    return ( 
        <div>


            <DynamicHeadTag
                headerText="CRM Employees"
                headerDescription="Manage and monitor your employees' payment activity."
            />
            

            <FormDrawer display="center" size="lg" isOpen={isModalOpenActivate} setIsOpen={() => setIsModalOpenActivate(false)}>
                <div className="w-full max-w-lg mx-auto relative bg-white rounded overflow-hidden">
                    <div className="h-12 px-4 bg-ep-primary flex justify-between overflow-hidden">
                        <div className="my-auto text-white">
                            Add {getFullName(employee.first_name, employee.middle_name, employee.last_name)} to Earnipay
                        </div>
                        <div onClick={() => setIsModalOpenActivate(false)} className="w-7 h-7 flex my-auto border group hover:bg-red-400 hover:border-red-400 border-white rounded-lg cursor-pointer">
                            <FontAwesomeIcon icon="times" className="text-white m-auto" />
                        </div>
                    </div>

                    <form className="p-6" onSubmit={submitForm}>
                        <div className="sm:grid sm:grid-cols-2 sm:gap-x-6">
                            <div>
                                <Input type="name" label="Full name" placeholder="Enter Employee Full Name" required={true} value={form.fullName} onChange={(e) => setForm({...form, fullName: e.target.value})} />
                                <Input type="email" label="Email Address" placeholder="Enter Employee Email Address" required={true} value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
                                <Input type="tel" label="Phone Number" placeholder="Enter Employee Phone Number" required={true} value={form.phoneNumber} onChange={(e) => setForm({...form, phoneNumber: e.target.value})} />
                                <Input type="text" label="Job Title" placeholder="Enter Employee Job Title" required={true} value={form.jobTitle} onChange={(e) => setForm({...form, jobTitle: e.target.value})} />
                                <Input type="text" label="Employee ID Number" placeholder="Enter Employee ID Number" value={form.employeeId} onChange={(e) => setForm({...form, employeeId: e.target.value})} />
                            </div>
                            <div>
                                <div>
                                    {(groupsLoading === true) && 
                                        <Input type="text" label="Employee Group" required={true} value="Loading groups..." disabled={true} />
                                    }
                                    {(groupsLoading === false) && 
                                        <SelectInput label="Employee Group" required={true} value={form.groupPolicyId} onChange={(e) => setForm({...form, groupPolicyId: e.target.value})}>
                                            <option value="">Select Group</option>
                                            <option value="general-group">General Group</option>
                                            {groups?.data?.map(group => (
                                                <option key={group.id} value={group.id}>{group.name}</option>
                                            ))}
                                        </SelectInput>
                                    }
                                </div>

                                <Input type="number" label="Net Monthly Salary" placeholder="Enter Employee Salary" min="1000" required={true} value={form.netMonthlySalary} onChange={(e) => setForm({...form, netMonthlySalary: e.target.value})} />
                                
                                <div>
                                    {/* {(banksLoading === true) && 
                                        <Input type="text" label="Bank Name" required={true} value="Loading banks..." disabled={true} />
                                    }
                                    {(banksLoading === false) && 
                                        <SelectInput label="Bank Name" required={true} value={form.bankCode} onChange={(e) => setForm({...form, bankCode: e.target.value})}>
                                            <option value="">Select Bank</option>
                                            {banks?.sort((a, b) => (a?.bankName || a?.institutionName)?.localeCompare(b?.bankName || b?.institutionName))?.map(bank => (
                                                <option key={bank.bankId} value={bank.bankCode}>{bank.bankShortName}</option>
                                            ))}
                                        </SelectInput>
                                    } */}
                                    <p className="text-sm mb-1">Bank Name <span className="form-input-required">*</span></p>
                                    <ReactSelect
                                        isDisabled={banksLoading}
                                        isLoading={banksLoading}
                                        isSearchable
                                        name="bankCode"
                                        onChange={(selected) => {
                                            setForm({
                                                ...form, 
                                                bankCode: selected.value,
                                            });
                                        }}
                                        options={banksData}
                                        styles={reactSelectStyles}
                                        placeholder={banksLoading ? "Fetching banks..." : "Select Bank"}
                                        noOptionsMessage={() => "No Banks Found"}
                                    />
                                </div>

                                <Input type="number" label="Bank Account Number" placeholder="Enter Bank Account Number" required={true} value={form.bankAccountNumber} onChange={(e) => setForm({...form, bankAccountNumber: e.target.value})} />
                            </div>
                        </div>

                        <div className="mt-2 flex space-x-4">
                            <ButtonLoader type="submit" loading={employeeCreateLoading} className="btn btn-ep-primary">
                                Add Employee
                            </ButtonLoader>
                            <button type="button" onClick={() => setIsModalOpenActivate(false)} className="btn btn-transparent-primary">
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </FormDrawer>


            <div className="lg:flex">
                <div className="w-full lg:w-1/2">
                    <div className="page-title">
                        CRM Employees
                        <FontAwesomeIcon data-tip data-for="info-enrolled-employees" icon="info-circle" className="ml-2 text-ep-secondary" />
                        <ReactToolTip id="info-enrolled-employees" className="tooltip">
                            These are your employees retrieved from your CRM: (crm_name)
                        </ReactToolTip>
                    </div>
                </div>
                <div className="w-full lg:w-1/2 flex mt-4 lg:mt-0 lg:justify-end space-x-3">
                    <Link to="/settings/integrations" className="btn btn-md btn-transparent-primary">
                        <FontAwesomeIcon icon="cog" className="w-3 h-3 mr-2 inline" />
                        Integration settings
                    </Link>
                </div>
            </div>


            <div className="mt-6">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    <DashboardBox
                        title="All CRM Employees"
                        loading={employeesLoading}
                        value={NumberFormat(employees.data?.length, "no-kobo") || "-"}
                        useDefaultIcon={true}
                    />
                </div>
            </div>


            <div className="mt-6 box">
                <div className="flex flex-wrap justify-between">
                    <div className="invisible max-w-sm flex-grow sm:mr-6">
                        <SearchBox placeholder="Search by name..." name="search" extraUrlParams="page=1" />
                    </div>
                    <div className="flex flex-wrap space-x-3 sm:space-x-6">
                        <div>
                            <DownloadAsCSV
                                ref={downloadAsCSVRef}
                                tooltip="Download CRM employees list"
                                filename="CRM Employees.csv"
                                columns={[
                                    { label: "Employee ID", key: "id" },
                                    { label: "First Name", key: "first_name" },
                                    { label: "Last Name", key: "last_name" },
                                    { label: "Middle Name", key: "middle_name" },
                                    { label: "Email Address", key: "emails[0].data" },
                                    { label: "Phone Number", key: "phone_numbers[0].data" },
                                    { label: "Gender", key: "gender" },
                                ]}
                                click={() => {
                                    dispatch(employeesActions.downloadGetAllCRMEmployees({...listParams, page: 1, limit: 10000})).then(() => {
                                        downloadAsCSVRef.current?.downloadData();
                                    });
                                }}
                                loading={employeesDownloadLoading}
                                response={employeesDownload?.data}
                            />
                        </div>

                        <div>
                            <Print component={printComponentRef} />
                        </div>
                    </div>
                </div>


                <div className="mt-6">
                    <div ref={printComponentRef}>
                        
                        <div className="mb-4 text-xl hidden print:block">
                            CRM Employees
                        </div>

                        <Table>
                            <Table.Head>
                                <th>Full Name</th>
                                <th>Email Address</th>
                                <th>Phone Number</th>
                                <th>Gender</th>
                                <th></th>
                            </Table.Head>
                            <Table.Body>

                                <TableContent
                                    loading={employeesLoading}
                                    loadingText={`Loading employees`}
                                    data={employees?.data}
                                    colspan={5}
                                />

                                {employeesLoading === false && employees?.data?.map((employee, index) =>
                                    <tr key={index}>
                                        <td>
                                            {getFullName(employee.first_name, employee.middle_name, employee.last_name)}
                                        </td>
                                        <td>
                                            {getEmailAddress(employee.emails)}
                                        </td>
                                        <td>
                                            {getPhoneNumbers(employee.phone_numbers)}
                                        </td>
                                        <td>
                                            <div className="capitalize">
                                                {employee.gender}
                                            </div>
                                        </td>
                                        <td>
                                            <button onClick={() => openModalActivate(employee)} className="btn btn-ep-primary btn-sm">
                                                Activate
                                            </button>
                                        </td>
                                    </tr>
                                )}

                            </Table.Body>
                        </Table>

                        <Maybe condition={employeesLoading === false && employees?.data?.length > 0}>
                            <div className="mt-6">
                                <Pagination data={employees?.data} />
                            </div>
                        </Maybe>

                    </div>
                </div>

            </div>


        </div>
    )
}


export default ListCRM;